import { FormCommonProps } from "../../../../common/forms/types.ts";
import { DeepPartial } from "react-hook-form";
import i18n from "i18next";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { CustomerFormApi, CustomerFormApi_InitData } from "src/api/generated/erp/sales/customer/api/customerFormApi.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { useMessageDialog } from "../../../../common/dialogs/messageDialog/MessageDialogContext.tsx";
import { CustomerFormContent, CustomerFormValues } from "src/components/views/erp/sales/customer/CustomerFormContent.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerFormProps extends FormCommonProps<number> {
	customerId?: number;
}

export const CustomerForm = (props: CustomerFormProps) => {
	const { customerId, onFormEdited, onCompleted } = props;
	const showMessageDialog = useMessageDialog();

	const { tenantConfig } = useTenantCustomizations();
	const useDefaultContact = tenantConfig.erp.customerFormUseDefaultContact;

	const { appUserId } = useGlobalInitData();

	return (
		<AsyncForm
			fetch={() => CustomerFormApi.getInitData({ customerId: customerId })}
			getDefaultValues={getDefaultValues}
			submit={submitForm}
			columns={2}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.customer.form"),
				data: {
					customerId: customerId ?? null,
				},
			}}
			render={({ data, ...formContentParams }) => (
				<CustomerFormContent
					{...formContentParams}
					isNewRecord={data.customer == null}
					initData={data}
				/>
			)}
		/>
	);

	function getDefaultValues(initData: CustomerFormApi_InitData): DeepPartial<CustomerFormValues> {
		if (initData.customer != null) {
			return {
				...initData.customer,
				invoiceSameAsDelivery: false,
				contacts: [],
			};
		} else {
			return {
				customerType: tenantConfig.erp.defaultCustomerType,
				language: "FINNISH",
				invoiceChannel: "EMAIL",
				discountPercentage: 0,
 				currencyId: initData.currencyOptions.find((currency) => currency.currencyCode === "EUR")?.currencyId,
				invoiceSameAsDelivery: false,
				deliveryAddress: {
					country: "FINLAND",
					phone: "",
					email: "",
				},
				invoiceAddress: {
					country: "FINLAND",
					phone: "",
					email: "",
				},
				paymentTermId: initData.paymentTermOptions.find((term) => term.isDefault)?.paymentTermId,
				salespersonId: initData.salespersonOptions.find((sp) => sp.appUserId === appUserId)?.salespersonId,
				contacts:
					useDefaultContact ?
						[
							{
								name: "",
								designation: "",
								phone: "",
								email: "",
								note: "",
								isContractSigner: true,
								isDefaultContact: true,
							},
						]
					:	[],
				defaultContactIndex: 0,
			};
		}
	}

	async function submitForm(values: CustomerFormValues) {
		const mappedValues = applyDefaultContactToAddresses(values);
		if (mappedValues === "interrupted") return "interrupted";

		const {
			invoiceSameAsDelivery,
			deliveryAddress,
			invoiceAddress,
			contacts,
			defaultContactIndex: _defaultContactIndex,
			...other
		} = mappedValues;

		const invoiceAddressToSave = invoiceSameAsDelivery ? deliveryAddress : (invoiceAddress ?? deliveryAddress);

		const customer = {
			...other,
			// Due to Jackson "single letter first word" -problem, we need to send both versions.
			einvoiceOperatorId: other.eInvoiceOperatorId,
		};

		if (other.customerId == null) {
			return await CustomerFormApi.insert({
				customer: customer,
				deliveryAddress: deliveryAddress,
				invoiceAddress: invoiceAddressToSave,
				contacts: contacts,
			});
		} else {
			await CustomerFormApi.update({
				customer: customer,
			});
			return other.customerId;
		}
	}

	function applyDefaultContactToAddresses(values: CustomerFormValues): CustomerFormValues | "interrupted" {
		if (!useDefaultContact || values.customerId != null) return values;

		const defaultContact = values.contacts[values.defaultContactIndex];
		if (defaultContact == null) {
			showMessageDialog({
				title: i18n.t("error"),
				content: i18n.t("default_contact_is_required"),
			});
			return "interrupted";
		}

		return {
			...values,
			contacts: values.contacts.map((contact, index) => ({
				...contact,
				isDefaultContact: index === values.defaultContactIndex,
			})),
			deliveryAddress: {
				...values.deliveryAddress,
				phone: defaultContact.phone,
				email: defaultContact.email,
				contact: defaultContact.name,
			},
			invoiceAddress: {
				...values.invoiceAddress,
				phone: defaultContact.phone,
				email: defaultContact.email,
				contact: defaultContact.name,
			},
		};
	}
};
